import React, { useState, useEffect } from 'react';
import { Rocket, DollarSign, Shield, BarChart3, Lock, Palette, Banknote, Cloud, Users, AlertTriangle, Check, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import apiService from '../services/api';

// =============================================================================
// EDUCATIONAL CARDS - Easy to update! Add/remove/edit as needed
// =============================================================================
const EDUCATION_CARDS = [
  {
    icon: DollarSign,
    title: 'USAGE-BASED PRICING',
    description:
      "You're billed for Monthly Active Users (MAU)—anyone who logs in that month. No login? They don't count. Simple as that.",
  },
  {
    icon: Shield,
    title: 'NO SURPRISE CUTOFFS',
    description:
      "If you exceed your plan, we give you breathing room—not a lockout. You'll get warnings as you approach, never a sudden shutoff.",
  },
  {
    icon: BarChart3,
    title: 'UNLIMITED WHERE IT MATTERS',
    description:
      "Unlimited dashboards, reports, and data connections. Build as much as you need—we don't charge per asset.",
  },
  {
    icon: Lock,
    title: 'BUILT-IN SECURITY',
    description:
      'Row-level security included from day one. Control data access by team, role, or client—enterprise-grade, no upgrade required.',
  },
  {
    icon: Palette,
    title: 'MAKE IT YOURS',
    description:
      'Use your own logo and colors. Custom domains included, even on Starter. Client-ready dashboards out of the box.',
  },
  {
    icon: Banknote,
    title: 'NO HIDDEN FEES',
    description:
      'Predictable monthly pricing as your team grows. Add occasional users without increasing your bill. Pay for usage, not headcount.',
  },
  {
    icon: Cloud,
    title: 'CLOUD-HOSTED',
    description:
      '100% cloud-hosted—nothing to install. Connect securely to cloud or on-prem databases. Automatic updates included.',
  },
  {
    icon: Users,
    title: 'BUILT FOR TEAMS',
    description:
      'Manage multiple teams or clients from one account. Invite your whole team—pay only for those who log in.',
  },
];

// =============================================================================
// DEPLOYMENT STEPS - Displayed as checklist
// =============================================================================
const DEPLOYMENT_STEPS = [
  'Provisioning dedicated environment',
  'Spinning up your private database',
  'Configuring dashboard engine',
  'Running security checks',
  'Preparing your admin account',
];

// =============================================================================
// COMPONENT
// =============================================================================
interface DeploymentProgressModalProps {
  open: boolean;
  workspaceId: number;
  workspaceName: string;
  onComplete: (deploymentUrl: string) => void;
}

interface DeploymentStatus {
  status: string;
  workspace_status: string;
  progress: number;
  step_label: string;
  error_message?: string;
}

const DeploymentProgressModal: React.FC<DeploymentProgressModalProps> = ({
  open,
  workspaceId,
  workspaceName,
  onComplete,
}) => {
  const [status, setStatus] = useState<DeploymentStatus>({
    status: 'pending',
    workspace_status: 'provisioning',
    progress: 0,
    step_label: 'Initializing deployment...',
  });
  const [error, setError] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [redirectMessage, setRedirectMessage] = useState('Deployment complete! Preparing your workspace...');
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [timerReset, setTimerReset] = useState(0);

  // Hover states
  const [prevHovered, setPrevHovered] = useState(false);
  const [nextHovered, setNextHovered] = useState(false);

  // Rotate cards every 10 seconds
  useEffect(() => {
    if (!open || isRedirecting || error) return;
    
    const interval = setInterval(() => {
      setCurrentCardIndex((prev) => (prev + 1) % EDUCATION_CARDS.length);
    }, 10000);

    return () => clearInterval(interval);
  }, [open, isRedirecting, error, timerReset]);

  const goToCard = (index: number) => {
    setCurrentCardIndex(index);
    setTimerReset((prev) => prev + 1);
  };

  const prevCard = () => goToCard((currentCardIndex - 1 + EDUCATION_CARDS.length) % EDUCATION_CARDS.length);
  const nextCard = () => goToCard((currentCardIndex + 1) % EDUCATION_CARDS.length);

  useEffect(() => {
    if (!open || !workspaceId) return;

    const pollStatus = async () => {
      try {
        const response = await apiService.get(`/workspaces/${workspaceId}/deployment`);
        const data: DeploymentStatus = response.data;
        
        setStatus(data);

        if ((data.status === 'completed' || data.workspace_status === 'active') && !isRedirecting) {
          setIsRedirecting(true);
          await performSSORedirect();
        } else if (data.status === 'failed') {
          setError(data.error_message || 'Deployment failed');
        }
      } catch (err: any) {
        console.error('Error polling deployment status:', err);
        setError(err.response?.data?.error || 'Failed to check deployment status');
      }
    };

    pollStatus();
    const interval = setInterval(pollStatus, 3000);
    return () => clearInterval(interval);
  }, [open, workspaceId, isRedirecting]);

  const performSSORedirect = async () => {
    let sessionToken: string | null = null;
    let deployment_url = '';
    
    try {
      setRedirectMessage('Getting your credentials...');
      
      const credsResponse = await apiService.post(`/workspaces/${workspaceId}/get-sso-credentials`, {});
      const { email, password, deployment_url: podUrl } = credsResponse.data;

      deployment_url = podUrl;

      if (!deployment_url) {
        throw new Error('Deployment URL not available');
      }

      setRedirectMessage('Logging you in...');

      const maxAttempts = 3;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          console.log(`SSO login attempt ${attempt}/${maxAttempts}...`);
          
          const loginResponse = await fetch(`${deployment_url}/api/login/DoLogin`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            mode: 'cors',
            body: JSON.stringify({
              bolForceLogin: true,
              isGuest: false,
              UserId: email,
              UserPassword: password
            })
          });

          if (loginResponse.ok) {
            const loginData = await loginResponse.json();
            
            if (loginData.LoginSession?.SessionToken) {
              sessionToken = loginData.LoginSession.SessionToken;
              console.log('SSO token obtained successfully');
              break;
            } else {
              console.log(`Attempt ${attempt}: Login response OK but no token`);
            }
          } else {
            console.log(`Attempt ${attempt}: Login failed with status ${loginResponse.status}`);
          }
          
          if (attempt < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        } catch (loginErr) {
          console.error(`Login attempt ${attempt} error:`, loginErr);
          if (attempt < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }

      if (!sessionToken) {
        console.log('Failed to get SSO token after all attempts, will redirect to login page');
      }
      
    } catch (err) {
      console.error('SSO preparation error:', err);
    }

    try {
      if (sessionToken && deployment_url) {
        setRedirectMessage('Opening your workspace...');
        console.log('Redirecting with SSO token');
        window.location.href = `${deployment_url}/sso.html?s=${sessionToken}&h=4`;
      } else if (deployment_url) {
        setRedirectMessage('Opening your workspace...');
        console.log('Redirecting without SSO token (user will need to login)');
        window.location.href = deployment_url;
      } else {
        console.log('No deployment URL, redirecting to dashboard');
        window.location.href = '/dashboard';
      }
    } catch (redirectErr) {
      console.error('Redirect failed:', redirectErr);
      setError('Unable to open workspace');
      setIsRedirecting(false);
    }
  };

  const getStepStatus = (stepIndex: number): 'complete' | 'active' | 'pending' => {
    const stepProgress = ((stepIndex + 1) / DEPLOYMENT_STEPS.length) * 100;
    if (status.progress >= stepProgress) return 'complete';
    if (status.progress >= (stepIndex / DEPLOYMENT_STEPS.length) * 100) return 'active';
    return 'pending';
  };

  if (!open) return null;

  // Styles
  const pageStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'var(--v2-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    zIndex: 1000,
    overflow: 'auto',
    fontFamily: 'var(--font-sans)',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--v2-border)',
    boxShadow: 'var(--shadow-lg)',
    padding: '40px',
    width: '100%',
    maxWidth: '520px',
  };

  const iconBoxStyle: React.CSSProperties = {
    width: '64px',
    height: '64px',
    borderRadius: 'var(--radius-lg)',
    background: 'linear-gradient(135deg, var(--v2-primary) 0%, #818cf8 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: 'var(--v2-text)',
    margin: '0 0 8px 0',
    textAlign: 'center',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '0.9375rem',
    color: 'var(--v2-text-tertiary)',
    margin: '0 0 32px 0',
    textAlign: 'center',
    lineHeight: 1.6,
  };

  const stepRowStyle = (stepStatus: 'complete' | 'active' | 'pending'): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0',
    color: stepStatus === 'pending' ? 'var(--v2-text-muted)' : stepStatus === 'active' ? 'var(--v2-primary)' : 'var(--v2-success)',
    fontWeight: stepStatus === 'active' ? 600 : 400,
    fontSize: '0.875rem',
  });

  const stepIconStyle = (stepStatus: 'complete' | 'active' | 'pending'): React.CSSProperties => ({
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: stepStatus === 'complete' ? 'var(--v2-success-bg)' : stepStatus === 'active' ? 'var(--v2-primary-light)' : 'var(--v2-bg)',
    color: stepStatus === 'complete' ? 'var(--v2-success)' : stepStatus === 'active' ? 'var(--v2-primary)' : 'var(--v2-text-muted)',
    border: stepStatus === 'active' ? '2px solid var(--v2-primary)' : 'none',
    flexShrink: 0,
  });

  const progressBarContainerStyle: React.CSSProperties = {
    marginTop: '24px',
    marginBottom: '24px',
  };

  const progressBarBgStyle: React.CSSProperties = {
    height: '8px',
    backgroundColor: 'var(--v2-border)',
    borderRadius: 'var(--radius-full)',
    overflow: 'hidden',
  };

  const progressBarFillStyle: React.CSSProperties = {
    height: '100%',
    backgroundColor: error ? 'var(--v2-error)' : isRedirecting ? 'var(--v2-success)' : 'var(--v2-primary)',
    borderRadius: 'var(--radius-full)',
    width: `${status.progress}%`,
    transition: 'width 0.5s ease, background-color 0.3s ease',
  };

  const progressTextStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.8125rem',
    color: 'var(--v2-text-tertiary)',
    marginTop: '8px',
  };

  const educationCardStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--v2-border)',
    boxShadow: 'var(--shadow-sm)',
    padding: '20px',
    marginBottom: '16px',
    minHeight: '120px',
  };

  const educationHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px',
  };

  const educationIconStyle: React.CSSProperties = {
    width: '32px',
    height: '32px',
    borderRadius: 'var(--radius-sm)',
    backgroundColor: 'var(--v2-primary-light)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const educationTitleStyle: React.CSSProperties = {
    fontSize: '0.6875rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--v2-primary)',
    margin: 0,
  };

  const educationDescStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    color: 'var(--v2-text)',
    lineHeight: 1.6,
    margin: 0,
  };

  const getNavArrowStyle = (hovered: boolean): React.CSSProperties => ({
    background: 'none',
    border: 'none',
    color: hovered ? 'var(--v2-text)' : 'var(--v2-text-tertiary)',
    backgroundColor: hovered ? 'var(--v2-surface-hover)' : 'transparent',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: 'var(--radius-sm)',
    transition: 'color 0.15s, background-color 0.15s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  const dotStyle = (isActive: boolean): React.CSSProperties => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: isActive ? 'var(--v2-primary)' : 'var(--v2-border)',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  });

  const infoCalloutStyle: React.CSSProperties = {
    padding: '12px 16px',
    borderRadius: 'var(--radius-md)',
    borderLeft: '4px solid var(--v2-primary)',
    backgroundColor: 'var(--v2-info-bg)',
    color: 'var(--v2-primary)',
    fontSize: '0.875rem',
  };

  const alertStyle = (type: 'error' | 'success'): React.CSSProperties => ({
    padding: '16px',
    borderRadius: 'var(--radius-md)',
    borderLeft: `4px solid ${type === 'error' ? 'var(--v2-error)' : 'var(--v2-success)'}`,
    backgroundColor: type === 'error' ? 'var(--v2-error-bg)' : 'var(--v2-success-bg)',
    color: type === 'error' ? 'var(--v2-error)' : 'var(--v2-success-dark)',
    fontSize: '0.875rem',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  });

  const currentCard = EDUCATION_CARDS[currentCardIndex];
  const CardIcon = currentCard.icon;

  return (
    <>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
      <div style={pageStyle}>
        <div style={cardStyle}>
          {/* Icon */}
          <div style={iconBoxStyle}>
            <Rocket size={32} strokeWidth={1.5} color="#ffffff" />
          </div>

          {/* Title */}
          <h1 style={titleStyle}>Deploying "{workspaceName}"</h1>
          
          {/* Subtitle */}
          <p style={subtitleStyle}>
            We're setting up your private workspace with dedicated infrastructure. 
            You'll be logged in automatically when ready.
          </p>

          {/* Error state */}
          {error && (
            <div style={alertStyle('error')}>
              <AlertTriangle size={18} strokeWidth={1.5} style={{ flexShrink: 0 }} />
              <span>{error}</span>
            </div>
          )}

          {/* Redirecting state */}
          {isRedirecting && !error && (
            <div style={alertStyle('success')}>
              <Check size={18} strokeWidth={2} style={{ flexShrink: 0 }} />
              <span>{redirectMessage}</span>
            </div>
          )}

          {/* Steps checklist */}
          {!isRedirecting && !error && (
            <div style={{ marginBottom: '8px' }}>
              {DEPLOYMENT_STEPS.map((step, index) => {
                const stepStatus = getStepStatus(index);
                return (
                  <div key={index} style={stepRowStyle(stepStatus)}>
                    <div style={stepIconStyle(stepStatus)}>
                      {stepStatus === 'complete' ? (
                        <Check size={12} strokeWidth={2.5} />
                      ) : stepStatus === 'active' ? (
                        <Loader2 size={12} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                      ) : (
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'var(--v2-text-muted)' }} />
                      )}
                    </div>
                    <span>{step}{stepStatus === 'active' ? '...' : ''}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Progress bar */}
          <div style={progressBarContainerStyle}>
            <div style={progressBarBgStyle}>
              <div style={progressBarFillStyle} />
            </div>
            <div style={progressTextStyle}>
              <span>{isRedirecting ? 'Complete!' : 'Usually takes 2-3 minutes'}</span>
              <span>{status.progress}%</span>
            </div>
          </div>

          {/* Educational card (rotating) with navigation */}
          {!isRedirecting && !error && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={prevCard}
                  onMouseEnter={() => setPrevHovered(true)}
                  onMouseLeave={() => setPrevHovered(false)}
                  style={getNavArrowStyle(prevHovered)}
                  aria-label="Previous tip"
                >
                  <ChevronLeft size={20} strokeWidth={1.5} />
                </button>
                <div style={{ ...educationCardStyle, flex: 1, marginBottom: 0 }} key={currentCardIndex}>
                  <div style={educationHeaderStyle}>
                    <div style={educationIconStyle}>
                      <CardIcon size={18} strokeWidth={1.5} color="var(--v2-primary)" />
                    </div>
                    <h3 style={educationTitleStyle}>{currentCard.title}</h3>
                  </div>
                  <p style={educationDescStyle}>{currentCard.description}</p>
                </div>
                <button
                  onClick={nextCard}
                  onMouseEnter={() => setNextHovered(true)}
                  onMouseLeave={() => setNextHovered(false)}
                  style={getNavArrowStyle(nextHovered)}
                  aria-label="Next tip"
                >
                  <ChevronRight size={20} strokeWidth={1.5} />
                </button>
              </div>
              {/* Dot indicators */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '12px', marginBottom: '20px' }}>
                {EDUCATION_CARDS.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToCard(index)}
                    style={dotStyle(index === currentCardIndex)}
                    aria-label={`Go to tip ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Redirecting spinner */}
          {isRedirecting && !error && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
              <Loader2 size={32} strokeWidth={2} color="var(--v2-primary)" style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          )}

          {/* Info callout */}
          {!error && (
            <div style={infoCalloutStyle}>
              <strong>Tip:</strong> We'll email you when ready—feel free to close this tab.
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DeploymentProgressModal;
